import {ModelSetOptions} from 'backbone';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as biojs from 'BioJS';
import {Sequence as ComponentSequence} from 'components/Sequence';
import {SearchOptions, SequenceMatch} from 'molbio/biological-search/Search';
import * as search from 'molbio/biological-search/SearchModels';
import * as sequtil from 'molbio/SeqUtil';
import ORF from 'molbio/translations/backbone/ORF';
import TranslationAsAnnotation, {
  getTranslationsAsAnnotationsInRange,
} from 'molbio/translations/backbone/TranslationAsAnnotation';
import {VisibilityOptionSettings} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {SingletonModel} from 'util/backbone-extensions';

const CUTOFF = 2000;
const FEAT_CUTOFF = 20;

export interface Range {
  start: number;
  end: number;
}

interface ResultOptions {
  searchMsg?: string;
  range?: Range | null;
}

// A function that returns the relevant filter function (matching the type of SearchOptions.filter) given a
// particular sequence
type SearchFilter = (seq: ComponentSequence) => (match: SequenceMatch) => boolean;

type SearchDNADataSearchOptions = Pick<SearchOptions, 'range' | 'dedup' | 'maxMismatches'> & {
  filter: SearchFilter;
  sequenceQuery: string;
};

export type SearchMode = 'search' | 'gotobase';

export type SearchRegion = 'all' | 'fwd' | 'rev' | 'orfs';

interface SearchDNADataAttributes {
  capped: boolean;
  cidToSeq: {[cid: string]: ComponentSequence};
  featCapped: boolean;
  featureResults: search.FeatureSearchResults;
  gotoFromBase: number | null;
  gotoFromBaseEnd: number | null;
  gotoMsg: string | null;
  hasSearched: boolean;
  index: number;
  maxMismatches: number | null;
  onlyShowSelected: boolean;
  query: string | null;
  range: Range | undefined | null;
  refCount: {[cid: string]: number};
  region: SearchRegion;
  results: search.DNASearchResults;
  searchAminoAcids: boolean;
  seqToGroup: {[cid: string]: number};
  sequence: ComponentSequence | null;
  showSettings: boolean;
  total: number;
  mode?: SearchMode | null;
  searchMsg?: string;
}

export interface SearchDNAData {
  get<K extends keyof SearchDNADataAttributes>(key: K): SearchDNADataAttributes[K];
  set(obj: Partial<SearchDNADataAttributes>, options?: ModelSetOptions): SearchDNAData;
  set<K extends keyof SearchDNADataAttributes>(
    attributeName: K,
    value: SearchDNADataAttributes[K],
    options?: ModelSetOptions
  ): SearchDNAData;
}

export class SearchDNAData extends SingletonModel {
  translationsAsAnnotations!: Array<TranslationAsAnnotation>;

  defaults(): Partial<SearchDNADataAttributes> {
    return {
      capped: false,
      cidToSeq: {},
      featCapped: false,
      featureResults: new search.FeatureSearchResults(),
      gotoFromBase: null,
      gotoFromBaseEnd: null,
      gotoMsg: null,
      hasSearched: false,
      index: 0,
      onlyShowSelected: false,
      query: null,
      refCount: {},
      region: 'all',
      results: new search.DNASearchResults([], {scope: {allSelected: [], activeSelectable: null}}),
      searchAminoAcids: false,
      seqToGroup: {},
      sequence: null,
      showSettings: true,
      total: 0,
    };
  }

  featureResults(): SearchDNADataAttributes['featureResults'] {
    return this.get('featureResults');
  }

  changeIndex(index: number): void {
    if (index < 0) {
      this.get('results').deselect();
      this.set('index', -1);
    } else {
      this.get('results').at(index).select();
    }
  }

  initialize(): void {
    this.translationsAsAnnotations = [];

    this.listenTo(
      this.get('results'),
      'change:selected',
      (model: search.DNASearchResult, selected: boolean) => {
        if (selected) {
          this.set({index: this.get('results').indexOf(model)});
        }
      }
    );

    this.listenTo(this.get('results'), 'reset', () => {
      this.trigger('reset:results');
      return this.set('index', -1);
    });
  }

  setSequence(sequence: ComponentSequence | null): void {
    if (this.get('sequence') != null) {
      this.stopListening(this.get('sequence')!._sequence());
    }
    if (sequence != null) {
      this.listenTo(sequence._sequence(), 'change:translationGroups', () => {
        this._setTranslationData();
      });
    }
    this.set('sequence', sequence);
    this._setTranslationData();
  }

  _setTranslationData(): void {
    if (this.get('sequence')) {
      this.translationsAsAnnotations = getTranslationsAsAnnotationsInRange(
        this.get('sequence')!,
        0,
        0,
        true
      ).shownTranslations;
    } else {
      this.translationsAsAnnotations = [];
    }
  }

  _setResults(results: Array<search.DNASearchResult>, options: ResultOptions = {}): void {
    let middle;
    this.set('searchMsg', options.searchMsg);
    this.set('range', options.range);
    if (results.length > CUTOFF) {
      results = results.slice(0, CUTOFF);
      this.set('capped', true);
    } else {
      this.set('capped', false);
    }
    this.set('onlyShowSelected', results.length > 100);

    // eslint-disable-next-line no-cond-assign
    if (!(results.length === (middle = this.get('results').length) && middle === 0)) {
      this.get('results').reset(results);
    }
  }

  addSequences(seqs: Array<ComponentSequence>, groupIndex: number = 0): void {
    const seqToGroup = this.get('seqToGroup');
    const refCount = this.get('refCount');
    const cidToSeq = this.get('cidToSeq');

    let hasChange = false;

    for (const seq of Array.from(seqs)) {
      const seqCid = seq.cid;
      // eslint-disable-next-line benchlint/no-in-operator
      if (!(seqCid in refCount)) {
        refCount[seqCid] = 0;
        cidToSeq[seqCid] = seq;
        hasChange = true;
        this.listenTo(seq, 'change:bases', () => {
          if (this.get('hasSearched')) {
            this.trigger('basesChanged');
          }
        });
      }
      refCount[seqCid]++;
      seqToGroup[seqCid] = groupIndex;
    }

    if (hasChange) {
      this.resetSearch();
    }
  }

  resetSearch(): void {
    this._setResults([]);
    this.changeIndex(-1);
    this.get('featureResults').reset([]);
    this.set({gotoFromBase: null, gotoFromBaseEnd: null, hasSearched: false});
  }

  removeSequence(...sequences: Array<ComponentSequence>): void {
    const refCount = this.get('refCount');
    const seqToGroup = this.get('seqToGroup');
    const cidToSeq = this.get('cidToSeq');

    let hasChange = false;
    for (const seq of Array.from(sequences)) {
      const seqCid = seq.cid;
      // eslint-disable-next-line benchlint/no-in-operator
      if (seqCid in refCount) {
        refCount[seqCid]--;
      }
      if (refCount[seqCid] === 0) {
        this.stopListening(cidToSeq[seqCid]);
        hasChange = true;
        delete refCount[seqCid];
        delete seqToGroup[seqCid];
        delete cidToSeq[seqCid];
      }
    }

    if (hasChange) {
      this.resetSearch();
    }
  }

  _searchFeatures(query: string, range?: Range | null): Array<search.FeatureSearchResult> {
    const sequence = this.get('sequence');
    assert(sequence != null, 'Expected SearchDNAData sequence to be present when calling _searchFeatures.');
    const features = [];
    for (const feature of [
      ...sequence.annotations().models,
      ...sequence.sequencePrimers().models,
      ...sequence.parts().models,
      ...this.translationsAsAnnotations,
    ]) {
      const index = feature.name().toLowerCase().indexOf(query.toLowerCase());
      const included =
        range == null || sequtil.isRangeOverlap(feature.start(), feature.end(), range.start, range.end);
      if (index > -1 && included) {
        if (features.length >= FEAT_CUTOFF) {
          this.set({featCapped: true});
          return features;
        }
        features.push(new search.FeatureSearchResult({feature, start: index, end: index + query.length}));
      }
    }

    this.set({featCapped: false});
    return features;
  }

  _searchBases(searchOptions: SearchDNADataSearchOptions): Array<search.DNASearchResult> {
    const {sequenceQuery, range, dedup, maxMismatches, filter} = searchOptions;
    const seqToGroup = this.get('seqToGroup');
    const cidToSeq = this.get('cidToSeq');
    let results = [];
    for (const cid of Object.keys(cidToSeq)) {
      const seq = cidToSeq[cid];
      const seqresults = seq.get('_sequence').search(sequenceQuery, {
        allowedChars: [],
        range,
        limit: CUTOFF + 1,
        dedup,
        aminoAcids: this.get('searchAminoAcids'),
        maxMismatches,
        filter: filter(seq),
      });
      for (const result of seqresults) {
        results.push(
          new search.DNASearchResult({
            start: result.index,
            end: (result.index + result.sequence!.length) % seq.length(),
            strand: result.strand,
            sequence: seq,
          })
        );
      }
    }
    results = _.chain(results)
      .sortBy((dnasearchresult) => dnasearchresult.start())
      .sortBy((dnasearchresult) => -seqToGroup[dnasearchresult.get('sequence').get('_sequence').cid])
      .value();
    return results;
  }

  doSearch(
    query: string,
    range: Range | null,
    options?: {
      dedup?: boolean;
      searchFeatures?: boolean;
      maxMismatches?: number;
    }
  ): void {
    const dedup = options?.dedup ?? false;
    const searchFeatures = options?.searchFeatures ?? false;
    const maxMismatches = options?.maxMismatches ?? 0;
    // Base query.
    const resultOptions: ResultOptions = {range};
    const sequenceQuery = query.replace(/\s/g, '');
    let results: Array<search.DNASearchResult> = [];
    if (sequenceQuery.length > 0) {
      const filter = (sequence: ComponentSequence): ((match: SequenceMatch) => boolean) => {
        switch (this.get('region')) {
          case 'all':
            return (match: SequenceMatch) => true;
          case 'fwd':
            return (match: SequenceMatch) => match.strand === 1;
          case 'rev':
            return (match: SequenceMatch) => match.strand === -1;
          case 'orfs': {
            const orfSettings = (
              sequence.isRNASequence() ? vo.RNA_ORFS.settings : vo.ORFS.settings
            ) as VisibilityOptionSettings;
            const minLength = orfSettings.findWhere({name: 'minimum-orf-length'}).get('value');
            const frame = orfSettings.findWhere({name: 'orf-frame'}).get('value');
            const startCodon = orfSettings.findWhere({name: 'orf-start-codon'}).get('value');
            const orfs = sequence
              .get('orfs')
              .sliceAndFilter(0, sequence.length(), {minLength, frame, sequence, startCodon});
            const seqLength = sequence.length();

            return function (match: SequenceMatch): boolean {
              const {strand} = match;
              const start = match.index;
              const end = start + match.sequence!.length;
              return _.any(orfs, (orf: ORF) => {
                // Same strand
                if (strand !== orf.strand()) {
                  return false;
                }
                // Contained
                if (!sequtil.isRangeWithin(start, end, orf.start(), orf.end())) {
                  return false;
                }
                // Compare 5' end of result and ORF, should be 3-bp apart.
                const startCodonDist =
                  strand === 1
                    ? sequtil.mod(start - orf.start(), seqLength)
                    : sequtil.mod(orf.end() - end, seqLength);
                return startCodonDist % 3 === 0;
              });
            };
          }
          default:
            throw new Error(`Unexpected search region: ${this.get('region')}`);
        }
      };
      const aminoAcids = this.get('searchAminoAcids');
      const validAA = aminoAcids && biojs.util.isValidAminoAcids(sequenceQuery);
      const validBases =
        !aminoAcids &&
        biojs.util.isValidSequence(
          biojs.util.reverseTranscribeRNA(sequenceQuery),
          biojs.util.NucleotideType.DNA
        );
      if (validAA || validBases) {
        try {
          results = this._searchBases({sequenceQuery, range, dedup, maxMismatches, filter});
        } catch (err) {
          if (err instanceof biojs.util.LongQueryException) {
            resultOptions.searchMsg = 'query too long';
          } else {
            throw err;
          }
        }
      }
    }
    this._setResults(results, resultOptions);

    // Then do annotation/primer query.
    let features = [];
    if (searchFeatures) {
      features = this._searchFeatures(query, range);
      this.get('featureResults').reset(features);
    }
  }
}

export default SearchDNAData;
